import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cookie } from '@playcastdotio/Cookie';
import { useState, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CookieManagerProps {
  children: ReactNode;
}

export const CookieManager = ({ children }: CookieManagerProps) => {
  const { set, get } = cookie();
  const [show, setShow] = useState(get('cookie-consent') !== 'true');

  const handleClose = () => {
    set('cookie-consent', 'true');
    setShow(false);
  };

  return (
    <>
      {children}
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className='fixed bottom-0 left-0 right-0 z-[9999] w-full md:bottom-6 md:left-auto md:right-6 md:w-auto md:max-w-[500px]'
            style={{
              fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
            }}
          >
            <div
              className='mx-4 rounded-lg p-6 shadow-2xl md:mx-0'
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div className='flex flex-col gap-4'>
                {/* Header */}
                <div className='flex items-start justify-between'>
                  <motion.h2
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className='text-lg font-bold text-white md:text-xl'
                    style={{ fontVariationSettings: "'wght' 700" }}
                  >
                    🍪 Playcast Uses Cookies
                  </motion.h2>
                  <button
                    onClick={handleClose}
                    className='ml-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10'
                    data-testid='cookie-manager-close-button'
                    aria-label='Close cookie notice'
                  >
                    <FontAwesomeIcon icon={faXmark} className='text-lg' />
                  </button>
                </div>

                {/* Content */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className='text-sm leading-relaxed text-white/90 md:text-base'
                  style={{ fontVariationSettings: "'wght' 400" }}
                >
                  We use cookies to improve your experience on Playcast. Some information may be shared with our partners for analytics and advertising purposes.
                </motion.p>

                {/* Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className='flex flex-col gap-3 sm:flex-row'
                >
                  <button
                    onClick={handleClose}
                    className='flex-1 rounded-full px-6 py-3 text-sm font-semibold transition-all hover:scale-105 active:scale-95'
                    style={{
                      backgroundColor: '#FBBF24',
                      color: '#161423',
                      fontVariationSettings: "'wght' 600",
                    }}
                    data-testid='cookie-manager-allow-all-button'
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleClose}
                    className='flex-1 rounded-full border-2 px-6 py-3 text-sm font-semibold transition-all hover:scale-105 hover:bg-white/10 active:scale-95'
                    style={{
                      borderColor: '#FBBF24',
                      color: '#FBBF24',
                      backgroundColor: 'transparent',
                      fontVariationSettings: "'wght' 600",
                    }}
                    data-testid='cookie-manager-necessary-only-button'
                  >
                    Necessary Only
                  </button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
